import React, { createContext, useContext, useMemo } from 'react';

import { useAuthSession, useOidcConfig, useSignOut } from '../api/hooks/auth.hook';
import type { AuthMeResponse } from '../api/services/auth';
import { getOidcLogoutUrl } from '../api/services/auth';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

type AuthContextValue = {
  user: AuthMeResponse | null;
  status: AuthStatus;
  refresh: () => Promise<unknown>;
  signOut: () => Promise<void>;
  signingOut: boolean;
  keycloakEnabled: boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const requireAuth =
    (import.meta.env.VITE_APP_AUTH_TYPE as string | undefined) === 'NONE' ? false : true;

  const devContextValue = useMemo<AuthContextValue>(
    () => ({
      user: {
        email: 'dev@local',
        role: 'admin',
        roles: { default: 'admin' },
        org_id: 'dev',
        org_role: 'admin',
        keycloak_enabled: false,
        plan_info: {
          plan: 'enterprise',
          max_members: null,
          max_projects: null,
          max_connectors: null,
          monthly_token_limit: null,
          audit_retention_days: 365,
          limit_enforcement: 'soft',
          features: {
            messaging: true,
            dm: true,
            api_keys: true,
            keycloak: true,
            custom_policies: true,
            org_admin_controls: true,
            invite_members: true,
          },
        },
      },
      status: 'authenticated',
      refresh: async () => Promise.resolve(),
      signOut: async () => Promise.resolve(),
      signingOut: false,
      keycloakEnabled: false,
    }),
    [],
  );

  const session = useAuthSession();
  const signOutMutation = useSignOut();
  const oidcConfig = useOidcConfig();

  const { data, isLoading, refetch } = session;
  const { mutateAsync, isLoading: signingOut } = signOutMutation;
  const keycloakEnabled = oidcConfig.data?.enabled ?? false;

  const authenticatedValue = useMemo<AuthContextValue>(() => {
    const status: AuthStatus = isLoading ? 'loading' : data ? 'authenticated' : 'unauthenticated';

    return {
      user: data ?? null,
      status,
      refresh: refetch,
      signingOut,
      keycloakEnabled,
      signOut: async () => {
        try {
          await mutateAsync();
        } catch (err) {
          console.error('Sign out failed', err);
        }
        // If Keycloak is enabled, redirect to Keycloak logout
        if (keycloakEnabled) {
          window.location.href = getOidcLogoutUrl();
        }
      },
    };
  }, [data, isLoading, mutateAsync, refetch, signingOut, keycloakEnabled]);

  if (!requireAuth) {
    return <AuthContext.Provider value={devContextValue}>{children}</AuthContext.Provider>;
  }

  return <AuthContext.Provider value={authenticatedValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
